import React from "react";
import { ToggleGroup, ToggleGroupItem } from "../../../../components/ui/toggle-group";
import { Editor } from "@tiptap/core";
import {
  BoldIcon,
  ItalicIcon,
  UnderlineIcon,
  ListIcon,
  ListOrderedIcon,
} from "../../../../components/icons";

export interface MentionEditorToolbarProps {
  editor: Editor;
  disabled?: boolean;
}

const marks = [
  { name: "bold", label: "Bold", icon: <BoldIcon /> },
  { name: "italic", label: "Italic", icon: <ItalicIcon /> },
  { name: "underline", label: "Underline", icon: <UnderlineIcon /> },
  { name: "bulletList", label: "Bullet list", icon: <ListIcon /> },
  {
    name: "orderedList",
    label: "Numbered list",
    icon: <ListOrderedIcon />,
  },
] as const;

type MarkName = (typeof marks)[number]["name"];

const toggle = (editor: Editor, name: MarkName) => {
  const chain = editor.chain().focus();
  switch (name) {
    case "bold":
      return chain.toggleBold().run();
    case "italic":
      return chain.toggleItalic().run();
    case "underline":
      return chain.toggleUnderline().run();
    case "bulletList":
      return chain.toggleBulletList().run();
    case "orderedList":
      return chain.toggleOrderedList().run();
  }
};

/**
 * Lean formatting toolbar: bold, italic, underline, bullet list, numbered list.
 *
 * The bordered toggle group, so the active marks read as pressed, drawn like every
 * other bordered toggle in the pane. It used to be a bordered ButtonGroup of
 * hand-styled IconButtons.
 *
 * The pressed state is the editor's, not the group's: `value` is read off the editor on
 * every render and a press runs the TipTap command, which is what changes it. The
 * group's own change callback is deliberately not used -- it would hold a second copy
 * of the marks that disagrees with the text the moment the cursor moves.
 */
const MentionEditorToolbar: React.FC<MentionEditorToolbarProps> = ({
  editor,
  disabled = false,
}) => {
  const active = marks.filter((m) => editor.isActive(m.name)).map((m) => m.name);

  return (
    <ToggleGroup
      type="multiple"
      variant="bordered"
      value={active}
      aria-label="Formatting"
      className="mb-2"
    >
      {marks.map((m) => (
        <ToggleGroupItem
          key={m.name}
          value={m.name}
          aria-label={m.label}
          title={m.label}
          disabled={disabled}
          onClick={() => toggle(editor, m.name)}
          className="h-7 px-1.5"
        >
          {m.icon}
        </ToggleGroupItem>
      ))}
    </ToggleGroup>
  );
};

export default MentionEditorToolbar;
